/**
 * 任意テキストテンプレートマスタ (`GET /free-text-templates`) の一覧を配る軽量コンテキスト。
 *
 * 放送地点マスタ > コンテンツ編集画面の任意テキスト放送「放送文面 (通常テキスト)」で、
 * 登録済みの文面を選択して流し込むために使う。
 *
 * ## なぜ props ではなくコンテキストなのか
 *
 * 入力欄は
 *
 *   BroadcastPointsPage → BroadcastPointForm → BroadcastContentsEditor
 *     → ContentPropertyFields → FreeTextFields
 *
 * と 4 階層下にあり、中間層に「使わないのに通すだけ」の props を増やしたくない。
 * `PhraseTemplateContext` / `TranslateContext` と同じ方式に揃える。
 *
 * Provider が無い環境 (単体テストで個別コンポーネントを描画した場合など) でも動くよう、
 * デフォルト値は空配列 (= 選択 UI を出さない) とする。
 * 取得に失敗しても空配列になるだけで、放送文面の入力・保存は妨げない。
 */
import { createContext, useContext, useMemo } from 'react';

import type { FreeTextTemplate } from '../api/types';

export interface FreeTextTemplateContextValue {
  /** 登録済みの任意テキストテンプレート (未取得 / 取得失敗は空配列) */
  templates: readonly FreeTextTemplate[];
}

const EMPTY_TEMPLATES: readonly FreeTextTemplate[] = [];

const FreeTextTemplateContext = createContext<FreeTextTemplateContextValue>({
  templates: EMPTY_TEMPLATES,
});

export function FreeTextTemplateProvider({
  templates,
  children,
}: {
  templates: readonly FreeTextTemplate[];
  children: React.ReactNode;
}): React.JSX.Element {
  const value = useMemo<FreeTextTemplateContextValue>(() => ({ templates }), [templates]);
  return (
    <FreeTextTemplateContext.Provider value={value}>{children}</FreeTextTemplateContext.Provider>
  );
}

/** 入力欄から、登録済みの任意テキストテンプレート一覧を取得する。 */
export function useFreeTextTemplates(): readonly FreeTextTemplate[] {
  return useContext(FreeTextTemplateContext).templates;
}
